'use client'

import Script from 'next/script'
import DidomiUI from 'ui/src/components/didomi/didomi'
import { publicRuntimeConfig } from './env'

// TODO copied from @next/third-parties as it has type issues

export type DidomiIds = {
    default: string
    consentOnEngagement: string
}

export const DIDOMI_IDS: DidomiIds = {
    default: 'CnUtpQ7h', // user must click "Agree" button on Didomi notice
    consentOnEngagement: 'YjTZPQHB', // consent is automatically granted on scroll or click
}

export const DIDOMI_REGION_STORAGE_KEY = 'giphy.cmp.region'

type GTMParams = {
    gtmId: string
    dataLayer?: string[]
    dataLayerName?: string
    auth?: string
    preview?: string
    didomiIds?: DidomiIds
}

let currDataLayerName: string | undefined = undefined

export function getDidomiBootstrapScript(didomiIds: DidomiIds) {
    return `
        function showBanner(id) {
            (function(e,tgt){
            var t=document.createElement("script");t.id="spcloader";t.type="text/javascript";t.async=true;t.src="https://sdk.privacy-center.org/"+e+"/loader.js?target_type=notice&target="+tgt;t.charset="utf-8";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n)})("8878f527-7872-49a5-abd4-f6b2e56cb64a",id);
        };
        function resetConsentAfterRegionChange(response) {
            var region = [response.continentCode || '', response.region || ''].join(':');
            if (region === ':') {
                return;
            }
            try {
                var previousRegion = window.localStorage.getItem('${DIDOMI_REGION_STORAGE_KEY}');
                if (previousRegion && previousRegion !== region) {
                    window.didomiOnReady = window.didomiOnReady || [];
                    window.didomiOnReady.push(function(Didomi) {
                        try {
                            Didomi.reset();
                            window.localStorage.setItem('${DIDOMI_REGION_STORAGE_KEY}', region);
                            // Didomi has already initialized the notice for this page load. Reload once with the
                            // cleared consent so the notice for the new region is evaluated and shown immediately.
                            window.location.reload();
                        } catch (error) {
                            // Keep the previous region so the next page load retries the consent reset.
                            console.error(error)
                        }
                    });
                } else {
                    window.localStorage.setItem('${DIDOMI_REGION_STORAGE_KEY}', region);
                }
            } catch (error) {
                // Storage can be unavailable in privacy-restricted browsers. Didomi can still initialize normally.
                console.error(error)
            }
        }
        fetch('https://giphy.com/api/geolocation')
            .then((resp) => resp.json())
            .then(function(response) {
            window.didomiConfig = {
                    notice: {
                        content: {
                            dismiss: {
                            en: 'Agree',
                            },
                            deny: {
                            en: 'Disagree',
                            },
                            learnMore: {
                            en: 'Manage Cookies',
                            }
                        }
                    }
                }
                resetConsentAfterRegionChange(response)
                if(response.region === 'CA' || response.continentCode === "EU"){
                showBanner("${didomiIds['default']}")
                } else {
                showBanner("${didomiIds['consentOnEngagement']}")
                }
            }).catch((err) => {
                showBanner("${didomiIds['default']}")
            })`
}

export function GoogleTagManager(props: GTMParams) {
    const { gtmId, didomiIds = DIDOMI_IDS, dataLayerName = 'dataLayer', auth, preview, dataLayer } = props

    if (currDataLayerName === undefined) {
        currDataLayerName = dataLayerName
    }

    const gtmLayer = dataLayerName !== 'dataLayer' ? `$l=${dataLayerName}` : ''
    const gtmAuth = auth ? `&gtm_auth=${auth}` : ''
    const gtmPreview = preview ? `&gtm_preview=${preview}&gtm_cookies_win=x` : ''

    return (
        // Order is important to Didomi: https://developers.didomi.io/cmp/web-sdk/getting-started#script
        <>
            <DidomiUI />
            <Script
                type="didomi/javascript"
                id="_next-gtm-init"
                dangerouslySetInnerHTML={{
                    __html: `
                    (function(w,l){
                        w[l]=w[l]||[];
                        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
                        ${dataLayer ? `w[l].push(${JSON.stringify(dataLayer)})` : ''}
                    })(window,'${dataLayerName}');`,
                }}
            />
            <Script
                type="didomi/javascript"
                id="_next-gtm"
                src={`https://www.googletagmanager.com/gtm.js?id=${gtmId}${gtmLayer}${gtmAuth}${gtmPreview}`}
            />
            {!publicRuntimeConfig.isPlaywright && (
                <Script
                    id="didomi"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: getDidomiBootstrapScript(didomiIds),
                    }}
                />
            )}
        </>
    )
}

export const sendGTMEvent = (data: Object) => {
    if (currDataLayerName === undefined) {
        console.warn(`@next/third-parties: GTM has not been initialized`)
        return
    }

    // @ts-ignore
    if (window[currDataLayerName]) {
        // @ts-ignore
        window[currDataLayerName].push(data)
    } else {
        console.warn(`@next/third-parties: GTM dataLayer ${currDataLayerName} does not exist`)
    }
}
